import * as React from "react"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  PerformanceRowList,
  PerformanceRowsSkeleton,
  useBudgetPerformance,
  type PerformanceRow,
} from "@/features/finance/overview/widgets/budget-performance-shared"

const MAX_ROWS = 5

/**
 * Fund Utilization — planned / spent / available per FUND.
 *
 * The fund half of the picture, and the one that DOES move when money is redesignated: `available` is the
 * fund's own allocation history (allocated − spent − reserved), the same number the spend gate enforces. It
 * deliberately differs from the income figures next door, which report what a source brought in.
 */
export function FundUtilizationWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const query = useBudgetPerformance()
  const currency = query.data?.currency ?? ""

  const rows = React.useMemo<PerformanceRow[] | undefined>(() => {
    if (!query.data) return undefined
    return [...query.data.byFund]
      .sort((a, b) => (b.utilizationPct ?? 0) - (a.utilizationPct ?? 0))
      .slice(0, MAX_ROWS)
      .map((fund) => ({
        id: fund.fundId,
        label: fund.fundName ?? fund.fundId,
        planned: fund.planned,
        actual: fund.actual,
        utilizationPct: fund.utilizationPct,
        // Neutral: a fund's utilization is not a pass/fail judgement the way a budget line's is.
        tone: "muted" as const,
        footnote: fund.fund
          ? t("widgets.fundUtilization.available", {
              amount: formatMoneySafe(fund.fund.available, currency),
            })
          : undefined,
      }))
  }, [query.data, currency, t])

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.funds) ? () => go(OverviewRoutes.funds) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(list) => list.length === 0}
      skeleton={<PerformanceRowsSkeleton />}
      errorTitle={t("widgets.fundUtilization.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.fundUtilization.empty.title")}
    >
      {(list) => <PerformanceRowList rows={list} currency={currency} />}
    </WidgetBoundary>
  )
}
